import {
  FC,
  JSXElementConstructor,
  ReactElement,
  ReactNode,
  Suspense,
  useState,
} from 'react'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import Loader from '../Loader'
import Sidebar from './Sidebar'
import TopPanelDesktop from './TopPanelDesktop'
import TopPanelMobile from './TopPanelMobile'
import { useLoadUserData } from './_methods'
import VerifyDialog from './Sidebar/Company/VerifyDialog'
import { isLogin } from 'utils/token'
import { useCheckDesktop, useCheckMobile } from 'utils/hooks/useMediaQuery'
import { Params, UIMatch, useMatches, useParams } from 'react-router-dom'
import { InteractiveTutorialStep } from 'store/tutorial/types'
import { getPersonCategory } from 'utils/company'
import Tutorial from 'components/Pages/Retail/GeoIndications/Tutorial'
import { useAppSelector } from 'store/hooks'
import { getUserRole } from 'store/me/selectors'
import NoOfferAlert from './Common/NoOfferAlert'
import VerificationAlert from './Common/VerificationAlert'
import VerificationAlertOffers from './Common/VerificationAlertOffers'

const Wrapper = styled.div<{ $isDesktop: boolean }>`
  width: 100dvw;
  height: 100dvh;
  overflow: hidden;
  display: flex;
  flex-direction: ${props => (props.$isDesktop ? 'row' : 'column')};
`

const LoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`

const Content = styled.div`
  height: 100%;
  width: 100%;
  min-width: 320px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: auto;
`

const fallback = (
  <LoaderWrapper>
    <Loader color='#3B42A5' size='32px' />
  </LoaderWrapper>
)

export type BreadcrumbItem = {
  name: string
  link?: string
}

export type AsideTutorialStep = {
  title: string
  text: string
  image: {
    [lang: string]: {
      mobile: string
      desktop: string
    }
  }
  components?: {
    [lang: string]: {
      readonly [tagName: string]: ReactElement<
        any,
        string | JSXElementConstructor<any>
      >
    }
  }
}

export type TutorialData =
  | {
      page: string
      aside: true
      steps: AsideTutorialStep[]
    }
  | {
      page: string
      steps: InteractiveTutorialStep[]
    }

interface MenuProps {
  children: ReactNode
}

export interface RouteHandle {
  crumb?: (data: {
    t: TFunction
    params: Readonly<Params<string>>
    loaderData: any
  }) => BreadcrumbItem | BreadcrumbItem[]
  getTutorialData?: (data: {
    t: TFunction
    isMobile: boolean
    category: string
    role: string
  }) => TutorialData
}

const useRouteBreadcrumbs = () => {
  const params = useParams()
  const { t } = useTranslation()
  const matches = useMatches() as UIMatch<unknown, RouteHandle>[]

  return matches
    .filter(match => match.handle?.crumb)
    .map(match => match.handle.crumb?.({ t, params, loaderData: match.data }))
}

const useRouteTutorial = () => {
  const { t } = useTranslation()
  const matches = useMatches() as UIMatch<unknown, RouteHandle>[]
  const isMobile = useCheckMobile()
  // TODO: Make this reactive
  const category = getPersonCategory()
  const role = useAppSelector(getUserRole)

  const matchWithTutorial = matches.find(match => match.handle?.getTutorialData)
  if (
    !matchWithTutorial ||
    !matchWithTutorial.handle ||
    !matchWithTutorial.handle.getTutorialData
  ) {
    return {
      tutorial: null,
      customTutorial: null,
    }
  }

  const tutorial = matchWithTutorial.handle.getTutorialData({
    t,
    isMobile,
    category,
    role,
  })

  if (tutorial.steps.length === 0) {
    return {
      tutorial: null,
      customTutorial: null,
    }
  }

  if ('aside' in tutorial) {
    return {
      tutorial: tutorial,
      customTutorial: null,
    }
  }
  return {
    tutorial: null,
    customTutorial: <Tutorial data={tutorial} />,
  }
}

const Menu: FC<MenuProps> = props => {
  const { children } = props

  const routeBreadcrumbs = useRouteBreadcrumbs()
  const { tutorial, customTutorial } = useRouteTutorial()

  const isGuest = !isLogin()
  if (isGuest) return null

  useLoadUserData()
  const isDesktop = useCheckDesktop()

  const TopPanel = isDesktop ? TopPanelDesktop : TopPanelMobile

  const [verifyDialogOpened, setVerifyDialogOpened] = useState(false)

  return (
    <Wrapper $isDesktop={isDesktop}>
      {isDesktop && <Sidebar onVerify={() => setVerifyDialogOpened(true)} />}
      <Content>
        <NoOfferAlert />
        <VerificationAlert onVerify={() => setVerifyDialogOpened(true)} />
        <VerificationAlertOffers onVerify={() => setVerifyDialogOpened(true)} />
        <TopPanel
          breadcrumbs={routeBreadcrumbs}
          tutorial={tutorial}
          customTutorial={customTutorial}
          onVerify={() => setVerifyDialogOpened(true)}
        />

        <PageContent className='popover-zone'>
          <Suspense fallback={fallback}>{children}</Suspense>
        </PageContent>
      </Content>
      {verifyDialogOpened && (
        <VerifyDialog onClose={() => setVerifyDialogOpened(false)} />
      )}
    </Wrapper>
  )
}

export default Menu
